import { useCallback } from 'react';

import {
  useChatsPageGetChatNotificationPreferencesV2LazyQuery,
  useChatsPageUpdateChatNotificationPreferencesV2Mutation,
  ChatsPageGetChatNotificationPreferencesV2Document,
} from '../../../generated';

export interface UseUpdateChatNotificationPreferencesConfig {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

export interface UpdateChatNotificationPreferencesInput {
  chatId: string;
  /**
   * The distributor USER whose order-transmission preference is toggled.
   * Membership of this id in `deselectedUsersList` drives that user's toggle;
   * unrelated users in the list are always preserved.
   */
  userId: string;
  isEnabled: boolean;
}

export const useUpdateChatNotificationPreferences = (
  config: UseUpdateChatNotificationPreferencesConfig = {}
) => {
  const { onSuccess, onError } = config;

  // Lazy-fetch the current deselectedUsersList so we can merge non-destructively
  const [fetchCurrentPrefs] =
    useChatsPageGetChatNotificationPreferencesV2LazyQuery({
      fetchPolicy: 'no-cache',
    });

  const [updatePreferences, { loading }] =
    useChatsPageUpdateChatNotificationPreferencesV2Mutation({
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
    });

  const updateNotificationPreferences = useCallback(
    async (input: UpdateChatNotificationPreferencesInput) => {
      try {
        // Fetch the current list so we don't overwrite other users' deselections
        const { data, error: fetchError } = await fetchCurrentPrefs({
          variables: { chatId: input.chatId },
        });

        // errorPolicy 'all' resolves instead of throwing — surface a failed
        // read so we never merge against a partially-loaded list.
        if (fetchError) {
          throw fetchError;
        }

        const currentList: string[] =
          data?.chatNotificationPreferencesV2?.newOrder?.deselectedUsersList ??
          [];

        let nextList: string[];
        if (input.isEnabled) {
          // Remove this user from the deselected list (re-enable notifications),
          // leaving every other user's deselection untouched.
          nextList = currentList.filter((id) => id !== input.userId);
        } else {
          // Add this user to the deselected list only if not already present.
          nextList = currentList.includes(input.userId)
            ? currentList
            : [...currentList, input.userId];
        }

        const result = await updatePreferences({
          variables: {
            input: {
              id: input.chatId,
              newOrder: {
                deselectedUsersList: nextList,
              },
            },
          },
          // Invalidate the V2 query cache so any subscriber gets fresh data
          refetchQueries: [
            {
              query: ChatsPageGetChatNotificationPreferencesV2Document,
              variables: { chatId: input.chatId },
            },
          ],
        });

        // Treat partial GraphQL failures as failures: with errorPolicy 'all'
        // the promise resolves even when the server returned field errors.
        if (result.errors && result.errors.length > 0) {
          throw new Error(result.errors.map((err) => err.message).join('; '));
        }
        if (!result.data?.updateChatNotificationPreferencesV2) {
          throw new Error('Notification preference update returned no data');
        }

        onSuccess?.();
      } catch (error) {
        const notificationError =
          error instanceof Error
            ? error
            : new Error('Failed to update notification preferences');
        onError?.(notificationError);
        throw notificationError;
      }
    },
    [fetchCurrentPrefs, updatePreferences, onSuccess, onError]
  );

  return {
    updateNotificationPreferences,
    isUpdating: loading,
  };
};
